/**
 * Structural validation beyond shape: the invariants that make a content
 * document safe to render and safe to edit. Shape errors come from zod;
 * the checks here are cross-field rules zod cannot express locally.
 *
 * Full HTML sanitization (attribute allowlists, URL schemes) is the content
 * service's job at write time; the schema layer enforces the invariants that
 * must NEVER be violated in any stored document.
 */
import { isCanonicalAnchor } from './anchor.js';
import {
  SiteContent,
  type FreeformSectionT,
  type PatternSectionT,
  type SectionT,
  type SiteContentT,
  type JsonValueT,
} from './types.js';

export interface ValidationIssue {
  /** Machine-readable code, stable across releases. */
  code: string;
  /** Where in the document ("pages/0/sections/2"). */
  where: string;
  message: string;
}

export interface ValidationResult {
  ok: boolean;
  issues: ValidationIssue[];
  /** The parsed document when shape-valid (even if invariants failed). */
  content?: SiteContentT;
}

/** Count non-overlapping occurrences of `needle` in `haystack` (no regex). */
function countOccurrences(haystack: string, needle: string): number {
  let count = 0;
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    count += 1;
    index = haystack.indexOf(needle, index + needle.length);
  }
  return count;
}

/** Resolve a JSON-pointer-style path ("/items/0/title") into a fields object. */
export function resolveFieldPath(fields: Record<string, JsonValueT>, path: string): JsonValueT | undefined {
  const parts = path.split('/').slice(1);
  let current: JsonValueT | undefined = fields as JsonValueT;
  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') return undefined;
    current = Array.isArray(current)
      ? current[Number(part)]
      : (current as { [key: string]: JsonValueT })[part];
  }
  return current;
}

function checkFreeformSection(section: FreeformSectionT, where: string, issues: ValidationIssue[]): void {
  const htmlLower = section.html.toLowerCase();
  if (htmlLower.includes('<script')) {
    issues.push({ code: 'freeform-script-forbidden', where, message: 'freeform html must not contain <script> — public output ships zero engine/author JS' });
  }
  if (htmlLower.includes('javascript:')) {
    issues.push({ code: 'freeform-js-url-forbidden', where, message: 'freeform html must not contain javascript: URLs' });
  }
  for (const entry of section.editableManifest) {
    const marker = `data-edit="${entry.fieldId}"`;
    const occurrences = countOccurrences(section.html, marker);
    if (occurrences !== 1) {
      issues.push({
        code: 'manifest-marker-mismatch',
        where: `${where}/editableManifest/${entry.fieldId}`,
        message: `expected exactly one ${marker} in html, found ${occurrences}`,
      });
    }
  }
}

function checkPatternSection(section: PatternSectionT, where: string, issues: ValidationIssue[]): void {
  for (const entry of section.editableManifest) {
    if (resolveFieldPath(section.fields, entry.path) === undefined) {
      issues.push({
        code: 'manifest-path-unresolved',
        where: `${where}/editableManifest/${entry.fieldId}`,
        message: `path ${entry.path} does not resolve in fields`,
      });
    }
  }
}

function checkSection(section: SectionT, where: string, issues: ValidationIssue[]): void {
  if (!isCanonicalAnchor(section.anchor)) {
    issues.push({ code: 'anchor-not-canonical', where, message: `anchor "${section.anchor}" is not in canonical form` });
  }
  const fieldIds = new Set<string>();
  for (const entry of section.editableManifest) {
    if (fieldIds.has(entry.fieldId)) {
      issues.push({ code: 'duplicate-field-id', where, message: `duplicate fieldId "${entry.fieldId}"` });
    }
    fieldIds.add(entry.fieldId);
  }
  if (section.kind === 'freeform') checkFreeformSection(section, where, issues);
  else checkPatternSection(section, where, issues);
}

/**
 * Validate a content document: zod shape first, then cross-field invariants.
 * Returns ALL issues found (never throws on invalid content).
 */
export function validateSiteContent(input: unknown): ValidationResult {
  const parsed = SiteContent.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((issue) => ({
        code: 'shape',
        where: issue.path.join('/'),
        message: issue.message,
      })),
    };
  }

  const issues: ValidationIssue[] = [];
  const content = parsed.data;
  const pagePaths = new Set<string>();

  content.pages.forEach((page, pageIndex) => {
    const pageWhere = `pages/${pageIndex}`;
    if (pagePaths.has(page.path)) {
      issues.push({ code: 'duplicate-page-path', where: pageWhere, message: `duplicate page path "${page.path}"` });
    }
    pagePaths.add(page.path);

    const anchors = new Set<string>();
    const sectionIds = new Set<string>();
    page.sections.forEach((section, sectionIndex) => {
      const where = `${pageWhere}/sections/${sectionIndex}`;
      if (anchors.has(section.anchor)) {
        issues.push({ code: 'duplicate-anchor', where, message: `duplicate anchor "${section.anchor}" on page "${page.path}"` });
      }
      anchors.add(section.anchor);
      if (sectionIds.has(section.id)) {
        issues.push({ code: 'duplicate-section-id', where, message: `duplicate section id "${section.id}"` });
      }
      sectionIds.add(section.id);
      checkSection(section, where, issues);
    });
  });

  return { ok: issues.length === 0, issues, content };
}
